import { useState, useEffect, useCallback } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2, Lock, RefreshCw, LogOut, Calendar, CheckCircle2, Clock, XCircle } from "lucide-react";

const STORAGE_KEY = "dvc_availability_pw";

const TABS = [
  { key: "this_week",    label: "This Week"    },
  { key: "next_week",    label: "Next Week"    },
  { key: "next_2_weeks", label: "Next 2 Weeks" },
  { key: "next_3_weeks", label: "Next 3 Weeks" },
  { key: "next_4_weeks", label: "Next 4 Weeks" },
];

// Returns the local YYYY-MM-DD string of `d` (no timezone conversion — d is already local)
const ymd = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

// ISO Monday of the week containing `d`
const isoMonday = (d) => {
  const copy = new Date(d);
  const day = copy.getDay(); // 0=Sun … 6=Sat
  const diff = day === 0 ? -6 : 1 - day; // shift to Monday
  copy.setDate(copy.getDate() + diff);
  copy.setHours(0, 0, 0, 0);
  return copy;
};

const filterDaysForTab = (allDays, tabKey) => {
  if (!allDays || allDays.length === 0) return [];
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const thisMon = isoMonday(today);
  const thisSun = new Date(thisMon); thisSun.setDate(thisSun.getDate() + 6);
  const nextMon = new Date(thisMon); nextMon.setDate(nextMon.getDate() + 7);
  const nextSun = new Date(nextMon); nextSun.setDate(nextSun.getDate() + 6);
  const in14 = new Date(today); in14.setDate(in14.getDate() + 14);
  const in21 = new Date(today); in21.setDate(in21.getDate() + 21);
  const in28 = new Date(today); in28.setDate(in28.getDate() + 28);

  const inRange = (dayStr, startDate, endDate) => {
    return dayStr >= ymd(startDate) && dayStr <= ymd(endDate);
  };

  switch (tabKey) {
    case "this_week":    return allDays.filter((d) => inRange(d.date, today, thisSun));
    case "next_week":    return allDays.filter((d) => inRange(d.date, nextMon, nextSun));
    case "next_2_weeks": return allDays.filter((d) => inRange(d.date, today, in14));
    case "next_3_weeks": return allDays.filter((d) => inRange(d.date, today, in21));
    case "next_4_weeks": return allDays.filter((d) => inRange(d.date, today, in28));
    default: return allDays;
  }
};

const formatDayLabel = (dateStr, dayName) => {
  const [y, m, d] = dateStr.split("-").map(Number);
  const dt = new Date(y, m - 1, d);
  const monthShort = dt.toLocaleDateString("en-GB", { month: "short" });
  return `${dayName.slice(0, 3)} ${d} ${monthShort}`;
};

export default function AvailabilityPage() {
  const [password, setPassword]     = useState(() => localStorage.getItem(STORAGE_KEY) || "");
  const [entering, setEntering]     = useState(() => !localStorage.getItem(STORAGE_KEY));
  const [pwInput, setPwInput]       = useState("");
  const [verifying, setVerifying]   = useState(false);
  const [pageError, setPageError]   = useState("");
  const [data, setData]             = useState(null);
  const [loading, setLoading]       = useState(false);
  const [activeTab, setActiveTab]   = useState("this_week");

  const load = useCallback(async (pwd) => {
    setLoading(true);
    setPageError("");
    try {
      const res = await api.getPublicAvailability(4, pwd);
      setData(res.data);
    } catch (err) {
      const status = err.response?.status;
      if (status === 401) {
        // Password was rotated by admin — clear and re-prompt
        localStorage.removeItem(STORAGE_KEY);
        setPassword("");
        setEntering(true);
        setPageError("Your saved password is no longer valid. Please re-enter it.");
      } else if (status === 403) {
        setPageError("The availability page is currently disabled by the coordinator.");
      } else {
        setPageError(err.response?.data?.detail || "Unable to load availability. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (password && !entering) load(password);
  }, [password, entering, load]);

  const handleUnlock = async (e) => {
    e?.preventDefault?.();
    if (!pwInput.trim()) return;
    setVerifying(true);
    try {
      await api.verifyAvailabilityPassword(pwInput.trim());
      localStorage.setItem(STORAGE_KEY, pwInput.trim());
      setPassword(pwInput.trim());
      setPwInput("");
      setEntering(false);
      toast.success("Access granted");
    } catch (err) {
      const status = err.response?.status;
      if (status === 403) {
        toast.error("The availability page is currently disabled by the coordinator.");
      } else {
        toast.error("Incorrect password");
      }
    } finally {
      setVerifying(false);
    }
  };

  const handleSignOut = () => {
    localStorage.removeItem(STORAGE_KEY);
    setPassword("");
    setData(null);
    setEntering(true);
  };

  // ── Password gate ─────────────────────────────────────────────────────────
  if (entering) {
    return (
      <div style={{ fontFamily: "'Public Sans', sans-serif" }} className="min-h-screen bg-[#fcfdfc] flex flex-col">
        <div className="h-52 md:h-64 bg-[#dcfce7] flex flex-col justify-center items-center px-4">
          <div className="text-center text-green-900">
            <h1 className="text-3xl sm:text-4xl font-bold tracking-tight" style={{ fontFamily: "'Outfit', sans-serif" }}>Volunteer Availability View</h1>
            <p className="text-lg mt-2 text-green-700 font-medium">Dodder Valley Cycling</p>
          </div>
        </div>
        <div className="flex-1 flex items-start justify-center px-4 py-10">
          <form onSubmit={handleUnlock} className="w-full max-w-sm bg-white border border-slate-100 rounded-2xl shadow-sm p-8 space-y-5" data-testid="availability-password-gate">
            <div className="flex flex-col items-center text-center gap-2">
              <div className="w-14 h-14 bg-green-50 rounded-full flex items-center justify-center">
                <Lock className="w-6 h-6 text-green-700" />
              </div>
              <h2 className="text-xl font-semibold text-slate-800" style={{ fontFamily: "'Outfit', sans-serif" }}>Volunteer Access</h2>
              <p className="text-sm text-slate-500">Enter the shared volunteer password to view available slots.</p>
            </div>
            <div>
              <Label className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Password</Label>
              <Input
                type="password"
                value={pwInput}
                onChange={(e) => setPwInput(e.target.value)}
                autoFocus
                data-testid="availability-password-input"
                className="mt-1 h-11 bg-slate-50 border-slate-200"
                placeholder="Ask the coordinator if unsure"
              />
              {pageError && <p className="text-xs text-red-600 mt-2">{pageError}</p>}
            </div>
            <Button type="submit" disabled={verifying || !pwInput.trim()} data-testid="availability-unlock-btn"
              className="w-full h-11 bg-[#15803d] hover:bg-green-800 text-white gap-2">
              {verifying ? <Loader2 className="w-4 h-4 animate-spin" /> : <Lock className="w-4 h-4" />}
              {verifying ? "Verifying..." : "Unlock"}
            </Button>
            <p className="text-xs text-slate-400 text-center">Your password will be remembered on this device.</p>
          </form>
        </div>
      </div>
    );
  }

  // ── Dashboard ─────────────────────────────────────────────────────────────
  const daysForTab = data ? filterDaysForTab(data.days, activeTab) : [];
  const generatedAt = data?.generated_at ? new Date(data.generated_at) : null;
  const tzLabel = data?.timezone ? `(${data.timezone})` : "";

  return (
    <div style={{ fontFamily: "'Public Sans', sans-serif" }} className="min-h-screen bg-[#fcfdfc]">
      {/* Hero */}
      <div className="h-40 md:h-52 bg-[#dcfce7] flex flex-col justify-center items-center px-4">
        <div className="text-center text-green-900">
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight" style={{ fontFamily: "'Outfit', sans-serif" }}>Available Slots</h1>
          <p className="text-lg mt-2 text-green-700 font-medium">Dodder Valley Cycling — For Volunteers</p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-8 space-y-6">
        {/* How to book — user's exact wording */}
        <div className="bg-amber-50 border-2 border-amber-300 rounded-xl p-5" data-testid="availability-how-to-book">
          <p className="text-sm text-amber-900 leading-relaxed">
            <strong>How to book:</strong> Slots you see here are provisional. To claim one, contact <a href="mailto:bookings@doddervalleycycling.com" className="underline font-semibold hover:text-amber-950">bookings@doddervalleycycling.com</a> or WhatsApp <a href="https://wa.me/353894066746" target="_blank" rel="noreferrer" className="underline font-semibold hover:text-amber-950">(089) 4066746</a>. <strong>Do not use the public booking form.</strong>
          </p>
        </div>

        {/* Tab switcher */}
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
          <div className="flex flex-wrap gap-1 px-3 py-3 border-b border-slate-100 bg-slate-50/50">
            {TABS.map((t) => (
              <button
                key={t.key}
                onClick={() => setActiveTab(t.key)}
                data-testid={`availability-tab-${t.key}`}
                className={`flex-1 min-w-[110px] px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  activeTab === t.key
                    ? "bg-[#15803d] text-white shadow"
                    : "text-slate-600 hover:bg-white hover:text-slate-800"
                }`}>
                {t.label}
              </button>
            ))}
          </div>

          {/* Meta bar */}
          <div className="px-5 py-3 flex flex-wrap items-center justify-between gap-2 text-xs text-slate-500 border-b border-slate-100">
            <div className="flex items-center gap-2">
              <Calendar className="w-3.5 h-3.5" />
              {daysForTab.length} day{daysForTab.length === 1 ? "" : "s"} shown {tzLabel}
              {generatedAt && <span className="text-slate-400">· updated {generatedAt.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}</span>}
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => load(password)}
                disabled={loading}
                data-testid="availability-refresh-btn"
                className="flex items-center gap-1 text-green-700 hover:text-green-900 disabled:opacity-50 font-medium">
                {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
                Refresh
              </button>
              <button
                onClick={handleSignOut}
                data-testid="availability-signout-btn"
                className="flex items-center gap-1 text-slate-500 hover:text-slate-700 font-medium">
                <LogOut className="w-3.5 h-3.5" />
                Sign out
              </button>
            </div>
          </div>

          {/* Day list */}
          {loading && !data ? (
            <div className="p-12 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-green-700" /></div>
          ) : pageError ? (
            <div className="p-8 text-center text-sm text-red-600">{pageError}</div>
          ) : daysForTab.length === 0 ? (
            <div className="p-8 text-center text-sm text-slate-400">No days to show in this range.</div>
          ) : (
            <ul className="divide-y divide-slate-100" data-testid="availability-day-list">
              {daysForTab.map((day) => (
                <li key={day.date} className="px-5 py-4 flex flex-col sm:flex-row sm:items-center gap-3">
                  <div className="w-full sm:w-40 shrink-0">
                    <div className="text-sm font-semibold text-slate-800">{formatDayLabel(day.date, day.day_name)}</div>
                    <div className="text-xs text-slate-400">{day.date}</div>
                  </div>
                  <div className="flex-1 min-w-0">
                    {day.closed ? (
                      <div className="flex items-center gap-2 text-slate-400 text-sm">
                        <XCircle className="w-4 h-4" />
                        <span>Closed</span>
                      </div>
                    ) : day.clear_all_day ? (
                      <div className="flex items-center gap-2 text-green-700 font-semibold text-sm" data-testid={`day-${day.date}-clear`}>
                        <CheckCircle2 className="w-4 h-4" />
                        <span>Clear all day — bike free any time slots run</span>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {day.free_slots.length > 0 ? (
                          <div className="flex flex-wrap gap-1.5">
                            {day.free_slots.map((slot) => (
                              <span key={slot} className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-semibold bg-green-50 text-green-700 border border-green-200">
                                {slot}
                              </span>
                            ))}
                          </div>
                        ) : (
                          <div className="flex items-center gap-2 text-red-600 text-sm font-medium">
                            <XCircle className="w-4 h-4" />
                            <span>Fully booked</span>
                          </div>
                        )}
                        {day.booked_windows && day.booked_windows.length > 0 && (
                          <div className="flex items-center gap-2 text-xs text-slate-400">
                            <Clock className="w-3.5 h-3.5" />
                            <span>Booked: {day.booked_windows.map((w) => `${w.start}–${w.end}`).join(" · ")}</span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        <p className="text-xs text-slate-400 text-center">
          Dodder Valley Cycling · Accessible Bike Booking · Volunteer preview page
        </p>
      </div>
    </div>
  );
}
